import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Checkbox } from '@els/els-react--checkbox';
import RsTableRow from '@Pages/RetroSynthesis/components/RsTable/RsTableRow';
import RsBuildingBlocks from '@Pages/RetroSynthesis/components/RsBuildingBlocks';
import ReactionDetailsComponent from '@Components/Results/reactions/reaction-details/reaction-details.component';
import ReactionDetailRow from '@Components/Results/reactions/reaction-details/ReactionDetailRow';
import MultiStepPreview from './MultiStepPreview';

function isConditionMultiStep(condition) {
  return !!condition?.multiStepDetails;
}

function PlanPublishedItem({ index, databaseId, data, selected, onSelectionChange }) {
  const { id: reactionId, reactants = [], details = [], largestProduct } = data || {};
  const conditions = details.slice(0, ReactionDetailsComponent.MINIMAL_VIEW_MAX_CONDITIONS_COUNT);
  const structures = reactants.map((reactant) => ({
    ...reactant,
    ...reactant.molImage,
    subDetails: reactant.details,
    rxn: reactant.id,
    databaseId,
  }));

  // Hack for recalculating rowspan for checkbox and structures columns
  const [rowSpan, setRowSpan] = useState(conditions.length + 1);
  const renderedRowsRef = useRef(conditions.map(() => 1));
  const recalculateRowspan = () => {
    const totalRows = renderedRowsRef.current.reduce((sum, rowsNumber) => sum + rowsNumber, 0);
    setRowSpan(totalRows + 1);
  };
  const onRenderedRowsNumberChange = (i, rows) => {
    renderedRowsRef.current[i] = rows;
    recalculateRowspan();
  };

  const isMultiStepReaction = isConditionMultiStep(conditions[0]);
  const checkboxRowSpan = isMultiStepReaction ? rowSpan + conditions.length - 1 : rowSpan;
  const structuresRowSpan = isMultiStepReaction ? 2 : rowSpan;

  return (
    <>
      <RsTableRow>
        <td rowSpan={checkboxRowSpan} className="c-els-table__cell c-els-table__basic-cell plan-published-items__checkbox-column">
          <Checkbox
            checked={selected}
            onChange={() => {
              onSelectionChange({ index, checked: !selected });
            }}
            aria-checked={selected}
          />
        </td>
        <td
          rowSpan={structuresRowSpan}
          className={cn('c-els-table__cell c-els-table__basic-cell plan-published-items__substances-column', {
            'plan-published-items__substance-with-multi-step-column': isMultiStepReaction && conditions.length > 1,
          })}>
          <RsBuildingBlocks structures={structures} showZoom={false} showOptions={false} showStepsCount={false} />
          {isMultiStepReaction && <MultiStepPreview detail={conditions[0]} databaseId={databaseId} index={0} />}
        </td>
        <td className="c-els-table__cell c-els-table__basic-cell hidden-table-cell" />
      </RsTableRow>
      {conditions.map((detail, i) => {
        // Create a unique key using detail.id if available, or fall back to a combination with index
        const safeReactionId = reactionId || 'no-reaction';
        const detailKey = detail.id ? `${safeReactionId}-${detail.id}` : `${safeReactionId}-condition-${i}`;

        return (
          <React.Fragment key={detailKey}>
            {isConditionMultiStep(detail) && i > 0 && (
              <RsTableRow>
                <td rowSpan="2" className="c-els-table__cell c-els-table__basic-cell plan-published-items__multi-step-column">
                  <MultiStepPreview detail={detail} databaseId={databaseId} index={i} />
                </td>
                <td className="c-els-table__cell c-els-table__basic-cell hidden-table-cell" />
              </RsTableRow>
            )}
            <ReactionDetailRow
              metadata={{
                context: 'reactions',
                databaseId,
              }}
              detail={detail}
              index={`${index}`}
              reactionId={reactionId}
              databaseId={databaseId}
              largestProduct={largestProduct}
              allowAdditionalInfoShow={false}
              onRenderedRowsNumberChange={(rows) => onRenderedRowsNumberChange(i, rows)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

PlanPublishedItem.propTypes = {
  index: PropTypes.object,
  databaseId: PropTypes.string,
  data: PropTypes.object,
  selected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
};

export default PlanPublishedItem;
